import { makeStyles, Typography, useTheme } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import {
  ON_ASSIGNMENT_STATUS,
  ON_ASSIGNMENT_STATUS_API,
  ON_ASSIGNMENT_STATUS_LIST,
  ON_ASSIGNMENT_STATUS_SHORT_TEXT,
  ON_BILLING_STATUS,
  ON_BILLING_STATUS_API,
  ON_BILLING_STATUS_LIST,
  ON_BILLING_STATUS_SHORT_TEXT,
  ON_TIME_STATUS,
  ON_TIME_STATUS_API,
  ON_TIME_STATUS_DROPOFF,
  ON_TIME_STATUS_PICKUP,
  ON_TIME_STATUS_SHORT_TEXT,
} from 'doc-mate-store/lib/constants/load';
import { User } from 'doc-mate-store/lib/models';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DataTableContext } from '../../contexts';
import { SavedTable } from '../../models/SavedTable';
import {
  colors,
  onAssignmentStatusColors,
  onBillingStatusColors,
  onTimeStatusColors,
} from '../../themes';
import StatusItem from './StatusItem';

type Props = {
  me?: User;
  loading: boolean;
};

const TrackerStatusBar: React.FC<Props> = ({ me, loading }) => {
  const theme = useTheme();
  const {
    preset,
    presetTables,
    savedTables,
    savedTable,
    setValues,
    createSavedTable,
  } = useContext(DataTableContext);
  const classes = useStyles({ me, loading });

  const statusColors = onTimeStatusColors(theme);
  const assignmentStatusColors = onAssignmentStatusColors(theme);
  const billingStatusColors = onBillingStatusColors(theme);

  const ref = useRef<HTMLDivElement>(null);
  const [showTop, setShowTop] = useState(false);
  const [showBottom, setShowBottom] = useState(false);

  const handleScroll = useCallback(() => {
    if (ref.current) {
      const { offsetHeight, scrollTop, scrollHeight } = ref.current;
      setShowTop(scrollTop > 0);
      setShowBottom(scrollTop + offsetHeight < scrollHeight);
    }
  }, []);

  const handleScrollUp = useCallback(() => {
    if (ref.current) {
      ref.current.scrollBy({
        top: -ref.current.offsetHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleScrollDown = useCallback(() => {
    if (ref.current) {
      ref.current.scrollBy({
        top: ref.current.offsetHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    let r: any = undefined;
    if (ref.current) {
      handleScroll();
      window.addEventListener('resize', handleScroll);
      ref.current.addEventListener('scroll', handleScroll);
      r = ref.current;
    }
    return () => {
      if (r) {
        window.removeEventListener('resize', handleScroll);
        r.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const handleStatusItemClick = (
    onTimeStatus:
      | ON_ASSIGNMENT_STATUS
      | ON_TIME_STATUS
      | ON_BILLING_STATUS
      | 'all'
      | 'active'
      | 'delivered'
      | 'completed'
      | 'rejected'
      | 'stale'
      | 'additional_billing_all'
      | 'additional_billing_pending'
      | 'additional_billing_approved'
      | 'additional_billing_denied'
      | 'additional_billing_invoiced'
      | 'additional_billing_billing_issue',
    isOnAssignment?: boolean,
    isOnAdditionalBilling?: boolean,
  ) => {
    const apiOnTimeStatus = ON_TIME_STATUS_API[onTimeStatus as ON_TIME_STATUS];
    const apiOnAssignmentStatus =
      ON_ASSIGNMENT_STATUS_API[onTimeStatus as ON_ASSIGNMENT_STATUS];

    const apiOnBillingStatus =
      ON_BILLING_STATUS_API[onTimeStatus as ON_BILLING_STATUS];

    if (isOnAdditionalBilling) {
      return setValues(undefined, onTimeStatus as string);
    }

    if (isOnAssignment && apiOnAssignmentStatus) {
      return setValues(undefined, apiOnAssignmentStatus);
    } else if (apiOnBillingStatus) {
      return setValues(undefined, apiOnBillingStatus);
    } else if (apiOnTimeStatus) {
      return setValues(undefined, apiOnTimeStatus);
    } else {
      return setValues(undefined, onTimeStatus as string);
    }
  };

  const handleSavedTableClick = (savedTable: SavedTable) => {
    setValues(savedTable);
  };

  const handleNewSavedTableClick = async () => {
    const savedTable = await createSavedTable();
    if (savedTable) {
      handleSavedTableClick(savedTable);
    }
  };

  return (
    <div ref={ref} className={classes.root}>
      <div
        className={classes.scrollerTop}
        onClick={handleScrollUp}
        style={{
          display: showTop ? 'inherit' : 'none',
        }}
      >
        <KeyboardArrowUpIcon fontSize="large" />
      </div>

      <div className={classes.container}>
        <div className={classes.sectionWrapper}>
          <Typography component="h2" variant="h6" className={classes.section}>
            Summary
          </Typography>

          {presetTables?.all?.is_visible && (
            <StatusItem
              active={!savedTable && preset === 'all'}
              color={statusColors[ON_TIME_STATUS.UNKNOWN]}
              loading={loading}
              count={presetTables ? presetTables.all.results : 0}
              label={'All'}
              onClick={() => handleStatusItemClick('all')}
            />
          )}
          {presetTables?.active?.is_visible && (
            <StatusItem
              active={!savedTable && preset === 'active'}
              color={statusColors[ON_TIME_STATUS.UNKNOWN]}
              loading={loading}
              count={presetTables ? presetTables.active.results : 0}
              label={'Active'}
              onClick={() => handleStatusItemClick('active')}
            />
          )}
          {presetTables?.delivered?.is_visible && (
            <StatusItem
              active={!savedTable && preset === 'delivered'}
              color={statusColors[ON_TIME_STATUS.DELIVERED]}
              loading={loading}
              count={presetTables ? presetTables.delivered.results : 0}
              label={'Delivered'}
              onClick={() => handleStatusItemClick('delivered')}
            />
          )}
          {presetTables?.completed?.is_visible && (
            <StatusItem
              active={!savedTable && preset === 'completed'}
              color={statusColors[ON_TIME_STATUS.UNKNOWN]}
              loading={loading}
              count={presetTables ? presetTables.completed.results : 0}
              label={'Completed'}
              onClick={() => handleStatusItemClick('completed')}
            />
          )}
          {(me?.isBroker || me?.isDispatcher) &&
            presetTables?.rejected?.is_visible && (
              <StatusItem
                active={!savedTable && preset === 'rejected'}
                color={statusColors[ON_TIME_STATUS.PICKUP_MISSED]}
                loading={loading}
                count={presetTables ? presetTables.rejected.results : 0}
                label={'Driver Rejected'}
                onClick={() => handleStatusItemClick('rejected')}
              />
            )}
        </div>

        {(() => {
          const hasVisibleAssignmentItem = ON_ASSIGNMENT_STATUS_LIST.some(
            oas => presetTables?.[ON_ASSIGNMENT_STATUS_API[oas]]?.is_visible,
          );
          const isStaleVisible = Boolean(presetTables?.stale?.is_visible);
          const isSectionVisible = hasVisibleAssignmentItem || isStaleVisible;

          if (!me || (!me.isDispatcher && !me.isBroker) || !isSectionVisible) {
            return null;
          }

          return (
            <div key="onAssignment" className={classes.sectionWrapper}>
              <Typography
                component="h2"
                variant="h6"
                className={classes.section}
              >
                Assignment
              </Typography>

              {ON_ASSIGNMENT_STATUS_LIST.map(oas => {
                const apiKey = ON_ASSIGNMENT_STATUS_API[oas];

                if (!presetTables?.[apiKey]?.is_visible) {
                  return null;
                }

                return (
                  <StatusItem
                    key={oas}
                    active={!savedTable && preset === apiKey}
                    color={assignmentStatusColors[oas]}
                    loading={loading}
                    count={presetTables?.[apiKey]?.results ?? 0}
                    label={ON_ASSIGNMENT_STATUS_SHORT_TEXT[oas]}
                    onClick={() => handleStatusItemClick(oas, true)}
                  />
                );
              })}

              {presetTables?.stale?.is_visible && (
                <StatusItem
                  active={preset === 'stale'}
                  color={
                    assignmentStatusColors[
                      ON_ASSIGNMENT_STATUS.ASSIGNMENT_UNASSIGNED
                    ]
                  }
                  loading={loading}
                  count={presetTables ? presetTables.stale.results : 0}
                  label={'Stale'}
                  onClick={() => handleStatusItemClick('stale')}
                />
              )}
            </div>
          );
        })()}

        {[
          [ON_TIME_STATUS_PICKUP, 'Pick-up'],
          [ON_TIME_STATUS_DROPOFF, 'Drop-off'],
        ].map(([statuses, title]) => {
          const filteredStatuses = ((statuses as any) as ON_TIME_STATUS[]).filter(
            ots =>
              ![
                ON_TIME_STATUS.DROPOFF_AT_RISK,
                ON_TIME_STATUS.PICKUP_AT_RISK,
                ON_TIME_STATUS.DROPOFF_DETENTION,
                ON_TIME_STATUS.PICKUP_DETENTION,
                ON_TIME_STATUS.DROPOFF_CHECKEDIN,
                ON_TIME_STATUS.PICKUP_CHECKEDIN,
                ON_TIME_STATUS.DROPOFF_EARLY,
                ON_TIME_STATUS.PICKUP_EARLY,
                ON_TIME_STATUS.DROPOFF_ON_TIME,
                ON_TIME_STATUS.PICKUP_ON_TIME,
              ].includes(ots),
          );

          const hasVisibleItems = filteredStatuses.some(
            ots => presetTables?.[ON_TIME_STATUS_API[ots]]?.is_visible,
          );

          if (!hasVisibleItems) {
            return null;
          }

          return (
            <div key={title} className={classes.sectionWrapper}>
              <Typography
                component="h2"
                variant="h6"
                className={classes.section}
              >
                {title}
              </Typography>

              {filteredStatuses.map(ots => {
                const apiKey = ON_TIME_STATUS_API[ots];

                if (!presetTables?.[apiKey]?.is_visible) {
                  return null;
                }

                return (
                  <StatusItem
                    key={ots}
                    active={preset === apiKey}
                    color={statusColors[ots]}
                    loading={loading}
                    count={presetTables?.[apiKey]?.results ?? 0}
                    label={ON_TIME_STATUS_SHORT_TEXT[ots]}
                    onClick={() => handleStatusItemClick(ots)}
                  />
                );
              })}
            </div>
          );
        })}

        {me && (me.isDispatcher || me.isBroker) && (
          <div key="onBilling" className={classes.sectionWrapper}>
            <Typography component="h2" variant="h6" className={classes.section}>
              Billing
            </Typography>

            {ON_BILLING_STATUS_LIST.map(oas => {
              const apiKey = ON_BILLING_STATUS_API[oas];

              // if (!presetTables?.[apiKey]?.is_visible) {
              //   return null;
              // }

              return (
                <StatusItem
                  key={oas}
                  active={!savedTable && preset === apiKey}
                  color={billingStatusColors[oas]}
                  loading={loading}
                  count={presetTables?.[apiKey]?.results ?? 0}
                  label={ON_BILLING_STATUS_SHORT_TEXT[oas]}
                  onClick={() => handleStatusItemClick(oas)}
                />
              );
            })}
          </div>
        )}

        {(() => {
          if (!me || (!me.isBroker && !me.isDispatcher)) {
            return null;
          }

          const additionalBillingItems = [
            { id: 'additional_billing_all', label: 'All' },
            { id: 'additional_billing_pending', label: 'Pending' },
            { id: 'additional_billing_billing_issue', label: 'Billing Issue' },
            { id: 'additional_billing_approved', label: 'Approved' },

            {
              id: 'additional_billing_denied',
              label: 'Denied',
            },
            {
              id: 'additional_billing_invoiced',
              label: 'Invoiced',
            },
          ];

          // const visibleItems = additionalBillingItems.filter(item => {
          //   const key = presetTables?.[item.id] ? item.id : null;
          //   return key ? Boolean(presetTables[key]?.is_visible) : false;
          // });

          // if (visibleItems.length === 0) {
          //   return null;
          // }

          return (
            <div key="onAdditionalBilling" className={classes.sectionWrapper}>
              <Typography
                component="h2"
                variant="h6"
                className={classes.section}
              >
                Additional Billing
              </Typography>

              {additionalBillingItems.map(item => {
                const apiKey = presetTables?.[item.id] ? item.id : 0;
                const tableData = presetTables?.[apiKey];

                return (
                  <StatusItem
                    key={item.id}
                    active={!savedTable && preset === apiKey}
                    color={
                      apiKey === 'additional_billing_denied'
                        ? theme.palette.error.main
                        : apiKey === 'additional_billing_invoiced'
                        ? '#9140dd'
                        : '#10425a'
                    }
                    loading={loading}
                    count={tableData?.results ?? 0}
                    label={item.label}
                    onClick={() =>
                      handleStatusItemClick(apiKey as any, false, true)
                    }
                  />
                );
              })}
            </div>
          );
        })()}

        <div className={classes.sectionWrapper}>
          <Typography component="h2" variant="h6" className={classes.section}>
            Saved Tables
          </Typography>

          {Array.from(savedTables.values()).map(st => (
            <StatusItem
              key={`savedTable-${st.id}`}
              active={savedTable && savedTable.id === st.id}
              color={statusColors[ON_TIME_STATUS.UNKNOWN]}
              loading={loading}
              count={st.results}
              label={st.name}
              onClick={() => handleSavedTableClick(st)}
            />
          ))}

          <StatusItem
            active={false}
            color={statusColors[ON_TIME_STATUS.UNKNOWN]}
            count="+"
            loading={loading}
            label="New Table"
            onClick={handleNewSavedTableClick}
          />
        </div>
      </div>

      <div
        className={classes.scrollerBottom}
        onClick={handleScrollDown}
        style={{
          display: showBottom ? 'inherit' : 'none',
        }}
      >
        <KeyboardArrowDownIcon fontSize="large" />
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: theme.spacing(10.5),
    background: 'transparent',
    minWidth: theme.spacing(10.5),
    overflowX: 'hidden',
    overflowY: 'scroll',
    position: 'relative',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    zIndex: theme.zIndex.appBar * 2,
  },
  container: {
    minHeight: '100%',
    width: theme.spacing(10),
    background: '#e6e6e7',
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: theme.palette.grey[400],

    '& $sectionWrapper:nth-child(2n+1)': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  sectionWrapper: {
    marginRight: -1,
    opacity: (props: Props) => (props.loading ? 0.5 : 1),
  },
  section: {
    color: colors.primary[900],
    cursor: 'default',
    fontSize: 12,
    // fontWeight: theme.typography.fontWeightBold,
    fontWeight: 700,
    padding: theme.spacing(1, 1, 1, 0),
    userSelect: 'none',
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  scrollerTop: {
    cursor: 'pointer',
    padding: theme.spacing(1),
    position: 'fixed',
    left: 0,
    background:
      'linear-gradient(180deg, #ebebec 0%, rgba(235, 235, 236, 0) 70%);',
    width: theme.spacing(10),
    textAlign: 'center',
    // fontWeight: theme.typography.fontWeightBold,
    fontWeight: 700,
    zIndex: 900000,

    '& > *': {
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
    },

    '&:hover': {
      background: colors.primary[600],
      color: theme.palette.getContrastText(colors.primary[600]),
    },

    '&:hover > *': {
      transform: 'translateY(-8px)',
    },
  },
  scrollerBottom: {
    cursor: 'pointer',
    padding: theme.spacing(1),
    position: 'fixed',
    bottom: 0,
    left: 0,
    background:
      'linear-gradient(180deg, rgba(235, 235, 236, 0) 0%, #ebebec 70%);',
    width: theme.spacing(10),
    textAlign: 'center',
    // fontWeight: theme.typography.fontWeightBold,
    fontWeight: 700,
    zIndex: 900000,

    '& > *': {
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
    },

    '&:hover': {
      background: colors.primary[600],
      color: theme.palette.getContrastText(colors.primary[600]),
    },

    '&:hover > *': {
      transform: 'translateY(8px)',
    },
  },
}));

export default observer(TrackerStatusBar);
